import PropTypes from 'prop-types';
import React, { Children, useCallback, useEffect, useRef, memo } from 'react';
import { List, Map } from 'immutable';

import AgGridTable from './AgGridTable';

/**
 * TableToAgGridTable row. Can display the default value if no key is specified on this row.
 *
 * @param props {Object} You can put any extra props here. They will be put into `<tr>` props.
 * @param props.data {Immutable.Map} Row data. Use Immutable.Map.
 * @param props.fields {String[]} Array defines what values would be displayed and order of it.
 * @param props.defaultValues {String[]} Array with default values for cell. If cell try access
 *     the key is not present in `data`, the cell will display the value from here.
 */
const TableToAgGridTableRow = props => {
  const { data, fields, defaultValues, index, ...rest } = props;
  const cells = fields.map((field, i) => {
    // If there is such field in data Map, use its value, else use default
    const fieldValue = data.get(field.name, defaultValues[i]);
    const value = typeof field.valueWrapper === 'function' ? field.valueWrapper(fieldValue, data, index) : fieldValue;

    return <td key={i}>{value}</td>;
  });

  return <tr {...rest}>{cells}</tr>;
};

TableToAgGridTableRow.propTypes = {
  data: PropTypes.instanceOf(Map).isRequired,
  defaultValues: PropTypes.arrayOf(PropTypes.string).isRequired,
  fields: PropTypes.arrayOf(PropTypes.object).isRequired,
  isEditing: PropTypes.bool,
};

/**
 * A Column component. Has no real render. It is using to definition of table's columns.
 *
 * @param props {Object} You can put any extra props here. They will be put into `<th>` props.
 * @param props.title {*} The title of column. It should be the string usual, but you can pass React.Component.
 * @param props.field {String} Field name, there component should get data for Row for this column.
 * @param [props.defaultValue=""] {String} Used as fallback. If no such field specified in Row, display this value.
 * @class
 */
const Column = () => {};

Column.propTypes = {
  defaultValue: PropTypes.string,
  field: PropTypes.string.isRequired,
  title: PropTypes.any.isRequired,
  valueWrapper: PropTypes.func,
};

/**
 * A Row component. Has no real render. It is using to definition of table's rows.
 *
 * @param props {Object} You can put any extra props here. They will be put into `<tr>` props.
 * @param props.data {Immutable.Map} Row data. Use Immutable.Map to provide values for the table.
 * @class
 */
const Row = () => {};

Row.propTypes = {
  data: PropTypes.instanceOf(Map).isRequired,
  onDoubleClick: PropTypes.func,
};

/**
 * TableToAgGridTable component. It is used to define table without write
 * table boilerplate (map values into cells and columns).
 * Just define Columns, and Rows as TableToAgGridTable's children.
 * If no Rows specified as a child - display 1 table row with "No rows to display" content.
 * **Warn** You should define all columns before rows.
 *
 * @param props {Object}.
 * @example
 *     import TableToAgGridTable, { Column, Row } from '../helpers/TableToAgGridTable'
 *     ...
 *     <TableToAgGridTable>
 *       <Column title="My column" field="field1"/>
 *       <Column title="My column 2" field="field2"/>
 *       <Column title="My column 3" field="field3" defaultValue="DEFAULT"/>
 *       <Row data={Immutable.fromJS({field1:"1_1", field2: "1_2", field3: "1_3"})}/>
 *       <Row data={Immutable.fromJS({field1:"2_1", field3: "2_3"})}/>
 *       <Row data={Immutable.fromJS({field1:"3_1", field2: "3_2"})}/>
 *     </TableToAgGridTable>
 */
const TableToAgGridTable = ({
  children,
  height,
  loading,
  sortable,
  page = 0,
  columns: columnsProp = List(),
  data: rowsProp = List(),
  onGridReady,
  autoSizeColumnsToFit = true,
  autoSizeColumnsForData,
  onColumnResize,
  ...rest
}) => {
  const apiRef = useRef(null);
  const columnsApiRef = useRef(null);

  /**
   * Make all columns with auto-sized width.
   *
   * @link https://www.ag-grid.com/javascript-grid-resizing Usage example and documentation here.
   */
  const autoSizeAllColumns = useCallback(() => {
    const allColumns = [];

    columnsApiRef.current.getAllColumns().map(c => allColumns.push(c.colId));
    columnsApiRef.current.autoSizeColumns(allColumns);
  }, []);

  const handleGridReady = useCallback(
    params => {
      apiRef.current = params.api;
      columnsApiRef.current = params.columnApi;

      if (onGridReady) {
        onGridReady(params);
      }

      if (autoSizeColumnsToFit) {
        params.api.sizeColumnsToFit();
      }
    },
    [onGridReady, autoSizeColumnsToFit],
  );

  const handleColumnResized = useCallback(
    params => {
      if (params.finished && onColumnResize) {
        onColumnResize(columnsApiRef.current.getAllColumns().map(c => ({ ...c.colDef, width: c.actualWidth })));
      }
    },
    [onColumnResize],
  );

  const getRowNodeId = useCallback(data => data.order?.toString(), []);

  useEffect(() => {
    if (!loading && autoSizeColumnsForData && columnsApiRef.current) {
      autoSizeAllColumns();
    }
  }, [loading, autoSizeColumnsForData, autoSizeAllColumns]);

  let columns = columnsProp;
  let rows = rowsProp;
  const rowClassNames = [];

  Children.forEach(children, child => {
    const { type } = child;

    if (type === Column) {
      const {
        props: { field, title, ...restProps },
      } = child;

      columns = columns.push({ ...restProps, headerName: title, field });

      return;
    }

    if (type === Row) {
      const {
        props: { className = '', data },
      } = child;

      rowClassNames.push(className);
      rows = rows.push(data);
    }
  });

  return (
    <AgGridTable
      {...rest}
      columnDefs={columns}
      getRowClass={params => rowClassNames[params.rowIndex]}
      getRowNodeId={getRowNodeId}
      isFetching={loading}
      layoutInterval={100}
      onColumnResized={handleColumnResized}
      onGridReady={handleGridReady}
      page={page}
      rowData={rows}
      sortable={sortable}
      style={{ height }}
      autoFit
    />
  );
};

TableToAgGridTable.propTypes = {
  autoSizeColumnsForData: PropTypes.bool,
  autoSizeColumnsToFit: PropTypes.bool,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};

export { Column, Row };
export default memo(TableToAgGridTable);
